import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useSyncExternalStore,
} from "react";
import { AvailablePaymentMethod } from "@/networking/carts.api";
import { PaymentMethodData } from "@/networking/checkout.api";

declare global {
  interface Window {
    MP_DEVICE_SESSION_ID?: string;
  }
}

type DeviceIdExtractor = () => string | undefined;

const DEVICE_ID_EXTRACTORS: Record<string, DeviceIdExtractor> = {
  mercado_pago_dlocal: () => window.MP_DEVICE_SESSION_ID,
};

const loadedScripts = new Set<string>();
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return loadedScripts;
}

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function extractScriptSrc(scriptTag: string): string | undefined {
  const match = scriptTag.match(/src=['"]([^'"]+)['"]/);
  return match ? match[1] : undefined;
}

export function usePaymentMethodScript(
  availablePaymentMethods: AvailablePaymentMethod[] | undefined,
  selectedPaymentMethodType: string | undefined,
) {
  const [scriptError, setScriptError] = useState<string | undefined>();
  const loadingRef = useRef<string | null>(null);

  const loadedScriptsSnapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );

  const selectedMethod = availablePaymentMethods?.find(
    (m) => m.type === selectedPaymentMethodType,
  );

  const scriptSrc = selectedMethod?.script
    ? extractScriptSrc(selectedMethod.script)
    : undefined;

  useEffect(() => {
    if (!scriptSrc) return;
    if (loadedScripts.has(scriptSrc)) return;
    if (loadingRef.current === scriptSrc) return;

    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existingScript) {
      loadedScripts.add(scriptSrc);
      notifyListeners();
      return;
    }

    loadingRef.current = scriptSrc;

    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;

    script.onload = () => {
      loadedScripts.add(scriptSrc);
      notifyListeners();
      setScriptError(undefined);
      loadingRef.current = null;
    };

    script.onerror = () => {
      setScriptError(`Failed to load payment script: ${scriptSrc}`);
      loadingRef.current = null;
    };

    document.head.appendChild(script);
  }, [scriptSrc]);

  const getPaymentMethodData = useCallback(():
    | PaymentMethodData
    | undefined => {
    if (!selectedPaymentMethodType) return undefined;

    const extractor = DEVICE_ID_EXTRACTORS[selectedPaymentMethodType];
    if (!extractor) return undefined;

    const deviceId = extractor();
    if (!deviceId) return undefined;

    return { device_id: deviceId };
  }, [selectedPaymentMethodType]);

  const isScriptLoaded = !scriptSrc || loadedScriptsSnapshot.has(scriptSrc);

  return {
    scriptError,
    getPaymentMethodData,
    isScriptLoaded,
  };
}
